import {ComponentProps} from 'react';

import {Colors, Note, Text} from '@optimaxdev/design-system';

import {PRESCRIPTION_STATUS} from 'constants/success';

import {flags} from 'libs/rollout';
import {getPrescriptionStatusAfterUpdateOrder} from 'libs/success/statuses/statuses';
import {OrderDetailsItemsType} from 'types/orderDetailsTypes';
import {FILL_IT, ItemsType, SEND_LATER, UPLOAD} from 'types/successPage';

export const NOTIFICATIONS: {
    [p: string]: {
        text: string;
        type: ComponentProps<typeof Note>['type'];
    };
} = {
    [FILL_IT]: {
        text: 'Prescription uploaded successfully. Confirm your prescription to proceed.',
        type: 'warning',
    },
    [PRESCRIPTION_STATUS.EYE_EXAM]: {
        text: 'Prescription confirmation required. A prescription renewal link has been sent to your inbox.',
        type: 'warning',
    },
    [PRESCRIPTION_STATUS.FILL_SUCCESS]: {
        text: "Prescription filled successfully. We'll send it to your eye doctor for verification.",
        type: 'success',
    },
    [PRESCRIPTION_STATUS.UPLOAD_SUCCESS]: {
        text: 'Prescription uploaded successfully',
        type: 'success',
    },
    [PRESCRIPTION_STATUS.VISIBLY]: {
        text: 'Prescription missing. Prescription renewal link sent to inbox.',
        type: 'warning',
    },
    [SEND_LATER]: {
        text: 'Your prescription is missing. Add it so we can ship out your contacts.',
        type: 'warning',
    },
    [UPLOAD]: {
        text: 'Prescription uploaded successfully',
        type: 'success',
    },
};

/**
 * Returns whether the color is not empty
 */
export const isValidColor = (color?: string) => Boolean(Number(color));

/**
 * Returns common text props for ContactsItemDetails
 */
export const getCommonTextProps = (isDesktop: boolean): ComponentProps<typeof Text> => ({
    as: 'p',
    color: isDesktop ? Colors.limedSpruce : Colors.tundora,
    lineHeight: isDesktop ? '24px' : '20px',
    margin: '0 0 4px',
    size: isDesktop ? '16px' : '14px',
});

const getStatusFromEyeExam = (status: Maybe<string>): Maybe<string> => {
    switch (status) {
        case 'eligible':
            return PRESCRIPTION_STATUS.VISIBLY;
        case 'created':
            return PRESCRIPTION_STATUS.EYE_EXAM;
        default:
            return null;
    }
};

const isFillSuccessWithDoctorDetails = (item: OrderDetailsItemsType): boolean => {
    const {hasDoctorDetails, prescription} = item;
    return Boolean(
        (flags.VistaDRdetails.isEnabled() &&
            hasDoctorDetails &&
            prescription?.send_by === FILL_IT) ||
            (hasDoctorDetails && prescription?.send_by === FILL_IT),
    );
};

/**
 * Returns the notification method for the item
 */
export const getNotificationMethod = (
    item: OrderDetailsItemsType,
    items: ItemsType,
    examStatusById: Maybe<string>,
) => {
    const updatedPrescriptionStatus = getPrescriptionStatusAfterUpdateOrder(items, item);
    const statusFromEyeExam = getStatusFromEyeExam(examStatusById);
    const {prescription} = item;

    if (isFillSuccessWithDoctorDetails(item)) {
        return PRESCRIPTION_STATUS.FILL_SUCCESS;
    }

    if (statusFromEyeExam) {
        return statusFromEyeExam;
    }

    return updatedPrescriptionStatus || prescription?.send_by || '';
};

/**
 * Returns the notification text for the item
 */
export const getNotificationText = (method: number | string, text: string) =>
    method === FILL_IT
        ? 'Prescription filled successfully. Confirm your prescription to proceed.'
        : text;
